import React, { useState, useEffect } from "react";
import { FileText, Save, Eye, EyeOff, Loader2, RefreshCw } from "lucide-react";
import { GetAxios, PutAxios } from "../../helpers/axiosHelpers";
import { toast } from "sonner";
import RichTextEditor from "../common/RichTextEditor";

/**
 * Generic bilingual (EN / JA) content page editor.
 *
 * Props:
 *   slug        – the content page slug, e.g. "privacy-policy"
 *   pageLabel   – human-readable label shown in the header, e.g. "Privacy Policy"
 */
const ContentPageEditor = ({ slug, pageLabel }) => {
  const [lang, setLang] = useState("en"); // "en" | "ja"

  // English fields
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");

  // Japanese fields
  const [titleJa, setTitleJa] = useState("");
  const [contentJa, setContentJa] = useState("");

  const [isPublished, setIsPublished] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchPage = async () => {
    setLoading(true);
    try {
      const res = await GetAxios(`/content-pages/${slug}`);
      if (res?.success && res.data) {
        setTitle(res.data.title || "");
        setContent(res.data.content || "");
        setTitleJa(res.data.titleJa || "");
        setContentJa(res.data.contentJa || "");
        setIsPublished(res.data.isPublished || false);
      }
    } catch (_) {
      // No page yet — use defaults
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error("English title is required");
      return;
    }
    setSaving(true);
    try {
      const res = await PutAxios(`/content-pages/${slug}`, {
        title,
        content,
        titleJa: titleJa || undefined,
        contentJa: contentJa || undefined,
        isPublished,
      });
      if (res?.success) {
        toast.success(`${pageLabel} saved successfully!`);
      } else {
        toast.error(res?.msg || "Save failed");
      }
    } catch (_) {
      toast.error("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="h-[60vh] flex flex-col items-center justify-center opacity-30">
        <div className="w-10 h-10 border-4 border-subidha-blue border-t-transparent rounded-full animate-spin mb-4" />
        <p className="font-black uppercase tracking-[0.3em] text-[10px]">
          Loading Editor…
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* ── Header Card ─────────────────────────────────────────────── */}
      <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-xl shadow-slate-200/60 p-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-subidha-blue/10 rounded-2xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-subidha-blue" />
            </div>
            <div>
              <h2 className="text-xl font-black text-slate-900">
                {pageLabel} Editor
              </h2>
              <p className="text-slate-500 text-sm font-medium mt-0.5">
                Manage your public{" "}
                <span className="font-bold">{pageLabel}</span> page (English
                &amp; Japanese)
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* Published Toggle */}
            <button
              onClick={() => setIsPublished((p) => !p)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-xl font-bold text-xs transition-all ${
                isPublished
                  ? "bg-green-100 text-green-700 hover:bg-green-200"
                  : "bg-slate-100 text-slate-500 hover:bg-slate-200"
              }`}
            >
              {isPublished ? (
                <Eye className="w-4 h-4" />
              ) : (
                <EyeOff className="w-4 h-4" />
              )}
              <span>{isPublished ? "Published" : "Draft"}</span>
            </button>

            {/* Refresh */}
            <button
              onClick={fetchPage}
              className="p-2 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-all"
              title="Reload from server"
            >
              <RefreshCw className="w-4 h-4" />
            </button>

            {/* Save */}
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center space-x-2 px-6 py-2.5 bg-subidha-blue text-white rounded-xl font-black text-xs hover:bg-blue-800 transition-all disabled:opacity-60"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              <span>{saving ? "Saving…" : "Save"}</span>
            </button>
          </div>
        </div>
      </div>

      {/* ── Language Tab Switcher ────────────────────────────────────── */}
      <div className="flex gap-2">
        <button
          onClick={() => setLang("en")}
          className={`px-5 py-2 rounded-xl font-black text-xs transition-all ${
            lang === "en"
              ? "bg-subidha-blue text-white shadow-lg shadow-blue-500/20"
              : "bg-white border border-slate-200 text-slate-500 hover:border-subidha-blue hover:text-subidha-blue"
          }`}
        >
          🇬🇧 English
        </button>
        <button
          onClick={() => setLang("ja")}
          className={`px-5 py-2 rounded-xl font-black text-xs transition-all ${
            lang === "ja"
              ? "bg-subidha-blue text-white shadow-lg shadow-blue-500/20"
              : "bg-white border border-slate-200 text-slate-500 hover:border-subidha-blue hover:text-subidha-blue"
          }`}
        >
          🇯🇵 Japanese
        </button>
      </div>

      {/* ── Editor Card ──────────────────────────────────────────────── */}
      <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-xl shadow-slate-200/60 p-8 space-y-6">
        {lang === "en" ? (
          <>
            {/* English Title */}
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                Page Title (English)
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-5 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-slate-900 font-bold focus:ring-4 focus:ring-subidha-blue/10 outline-none transition-all"
                placeholder="e.g. Privacy Policy"
              />
            </div>

            {/* English Content */}
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                Content (English)
              </label>
              <RichTextEditor
                value={content}
                onChange={setContent}
                placeholder="Write the English content here…"
              />
            </div>
          </>
        ) : (
          <>
            {/* Japanese Title */}
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                ページタイトル (Japanese)
              </label>
              <input
                type="text"
                value={titleJa}
                onChange={(e) => setTitleJa(e.target.value)}
                className="w-full px-5 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-slate-900 font-bold focus:ring-4 focus:ring-subidha-blue/10 outline-none transition-all"
                placeholder="例：プライバシーポリシー"
              />
            </div>

            {/* Japanese Content */}
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                コンテンツ (Japanese)
              </label>
              <RichTextEditor
                value={contentJa}
                onChange={setContentJa}
                placeholder="日本語のコンテンツをここに入力してください…"
              />
            </div>
          </>
        )}

        {/* Status hint */}
        {isPublished ? (
          <p className="text-xs text-slate-400 font-medium">
            ✅ This page is published and visible at{" "}
            <a
              href={`/${slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-subidha-blue underline"
            >
              /{slug}
            </a>
          </p>
        ) : (
          <p className="text-xs text-slate-400 font-medium">
            ⚠️ This page is a draft and not publicly visible. Toggle "Published"
            to make it live.
          </p>
        )}
      </div>
    </div>
  );
};

export default ContentPageEditor;
